import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import {
  CreateOrganizationAttributesDTO,
  OrganizationDTO,
  OrganizationInvitesApi,
  OrganizationSettingsDTOCommissionRuleEnum,
  OrganizationSettingsDTOMaximumPolicyCoverEnum,
  OrganizationSettingsDTOPricingRuleEnum,
  OrganizationsApi,
  OrganizationUserDTO,
  SearchHubspotCompaniesResultsDTO,
  SearchOrganizationResultsDTO,
  UpdateOrganizationCommissionRuleDTOCommissionRuleEnum,
  UpdateOrganizationPricingRuleDTOPricingRuleEnum,
} from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { get } from 'lodash';
import { call, put, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { dismissFlashMessage, FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { fetchOrganizationInvitesRequested } from '../invite/invite.actions';
import { setCurrentModal } from '../modal/modal.actions';
import SCHEMA from '../schema';
import { fetchOrganizationUsersRequested } from '../user/user.actions';
import { createOrganizationInviteApi, createOrganizationsApi, runSagaWithAuth } from '../utils/api.utils';
import {
  CREATE_ORGANIZATION_API_REQUESTED,
  CREATE_ORGANIZATION_INVITE_API_REQUESTED,
  CREATE_ORGANIZATION_INVITE_STORE_KEY,
  CREATE_ORGANIZATION_STORE_KEY,
  createOrganizationFailed,
  createOrganizationInviteFailed,
  createOrganizationInviteSuccess,
  createOrganizationSuccess,
  FETCH_ORGANIZATION_BY_ID_API_REQUESTED,
  FETCH_ORGANIZATION_USERS_WITH_ROLES_API_REQUESTED,
  fetchOrganizationByIdFailed,
  fetchOrganizationByIdSuccess,
  fetchOrganizationUsersWithRolesFailed,
  fetchOrganizationUsersWithRolesSuccess,
  OrganizationActionTypes,
  searchHubspotCompaniesFailed,
  searchHubspotCompaniesRequested,
  searchHubspotCompaniesSuccess,
  UPDATE_ORG_ADDRESS_STORE_KEY,
  UPDATE_ORG_NOTES_STORE_KEY,
  UPDATE_ORGANIZATION_COMMISSION_RULE_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_COMMISSION_RULE_STORE_KEY,
  UPDATE_ORGANIZATION_HUBSPOT_ID_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY,
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY,
  UPDATE_ORGANIZATION_NAME_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_NAME_STORE_KEY,
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY,
  UPDATE_ORGANIZATION_PRICING_RULE_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY,
  updateOrganizationAddressFailed,
  updateOrganizationAddressRequested,
  updateOrganizationAddressSuccess,
  updateOrganizationCommissionRuleFailed,
  updateOrganizationCommissionRuleRequested,
  updateOrganizationCommissionRuleSuccess,
  updateOrganizationHubspotIdFailed,
  updateOrganizationHubspotIdRequested,
  updateOrganizationHubspotIdSuccess,
  updateOrganizationInventoryConfirmationDelayFailed,
  updateOrganizationInventoryConfirmationDelayRequested,
  updateOrganizationInventoryConfirmationDelaySuccess,
  updateOrganizationNameFailed,
  updateOrganizationNameRequested,
  updateOrganizationNameSuccess,
  updateOrganizationNotesFailed,
  updateOrganizationNotesRequested,
  updateOrganizationNotesSuccess,
  updateOrganizationPolicyCoverCapFailed,
  updateOrganizationPolicyCoverCapRequested,
  updateOrganizationPolicyCoverCapSuccess,
  updateOrganizationPricingRuleFailed,
  updateOrganizationPricingRuleRequested,
  updateOrganizationPricingRuleSuccess,
} from './organization.actions';
import {
  CreateOrganizationInvitePayload,
  CreateOrganizationPayload,
  EditableCommissionRule,
  FetchOrganizationByIdRequestedPayload,
  SearchHubspotCompaniesPayload,
  UpdateOrganizationAddressPayload,
  UpdateOrganizationCommissionRulePayload,
  UpdateOrganizationHubspotIdPayload,
  UpdateOrganizationInventoryConfirmationDelayPayload,
  UpdateOrganizationNamePayload,
  UpdateOrganizationNotesPayload,
  UpdateOrganizationPolicyCoverCapPayload,
  UpdateOrganizationPricingRulePayload,
} from './organization.types';

// ****************
// WORKERS
// ****************

const updateOrganizationPricingRuleEnumBySettingsEnum: Record<
  OrganizationSettingsDTOPricingRuleEnum,
  UpdateOrganizationPricingRuleDTOPricingRuleEnum
> = {
  [OrganizationSettingsDTOPricingRuleEnum.STANDARD]: UpdateOrganizationPricingRuleDTOPricingRuleEnum.STANDARD,
  [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP250]: UpdateOrganizationPricingRuleDTOPricingRuleEnum.STANDARDCAP250,
  [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP300]: UpdateOrganizationPricingRuleDTOPricingRuleEnum.STANDARDCAP300,
  [OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP350]: UpdateOrganizationPricingRuleDTOPricingRuleEnum.STANDARDCAP350,
  [OrganizationSettingsDTOPricingRuleEnum.NOMINALPERTENANT]: UpdateOrganizationPricingRuleDTOPricingRuleEnum.NOMINALPERTENANT,
  [OrganizationSettingsDTOPricingRuleEnum.STANDARD15PERCENTDISCOUNT]:
    UpdateOrganizationPricingRuleDTOPricingRuleEnum.STANDARD15PERCENTDISCOUNT,
};

const updateOrganizationPolicyCoverCapEnumBySettingsEnum: Record<
  OrganizationSettingsDTOMaximumPolicyCoverEnum,
  OrganizationSettingsDTOMaximumPolicyCoverEnum
> = {
  [OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS]:
    OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS,
  [OrganizationSettingsDTOMaximumPolicyCoverEnum.TENTHOUSANDPOUNDS]:
    OrganizationSettingsDTOMaximumPolicyCoverEnum.TENTHOUSANDPOUNDS,
};

const updateOrganizationCommissionRuleEnumBySettingsEnum: Record<
  EditableCommissionRule,
  UpdateOrganizationCommissionRuleDTOCommissionRuleEnum
> = {
  [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE20]:
    UpdateOrganizationCommissionRuleDTOCommissionRuleEnum.FEEPERCENTAGE20,
  [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE15]:
    UpdateOrganizationCommissionRuleDTOCommissionRuleEnum.FEEPERCENTAGE15,
  [OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE12POINT5]:
    UpdateOrganizationCommissionRuleDTOCommissionRuleEnum.FEEPERCENTAGE12POINT5,
  [OrganizationSettingsDTOCommissionRuleEnum.NOCOMMISSION]: UpdateOrganizationCommissionRuleDTOCommissionRuleEnum.NOCOMMISSION,
};

export function* fetchOrganizationById({ payload }: { type: string; payload: FetchOrganizationByIdRequestedPayload }) {
  try {
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const apiResponse: AxiosResponse<SearchOrganizationResultsDTO> = yield call(
      runSagaWithAuth(() => organizationsApi.findOrganizationById(payload.organizationId)),
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.organization);
    yield put<OrganizationActionTypes>(fetchOrganizationByIdSuccess());
  } catch (e) {
    yield put<OrganizationActionTypes>(fetchOrganizationByIdFailed(get(e, 'response.data.message', e)));
  }
}

export function* createOrganization({ payload }: { type: string; payload: CreateOrganizationPayload }) {
  try {
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const organizationInviteApi: OrganizationInvitesApi = yield createOrganizationInviteApi();
    let attributes: CreateOrganizationAttributesDTO = {};
    attributes = payload.hubspotId ? { hubspotId: payload.hubspotId } : {};

    const createOrgApiResponse: AxiosResponse<OrganizationDTO> = yield call(
      runSagaWithAuth(() =>
        organizationsApi.createOrganization({
          name: payload.organizationName,
          attributes,
          settings: {},
          type: payload.type,
          integratorId: payload.integratorId,
          internalSalesOwnerId: payload.salesOwnerId,
          primaryVertical: payload.primaryVertical,
          secondaryVerticals: payload.secondaryVerticals,
          organizationGroupId: payload.organizationGroupId,
          parentOrganizationGroupId: payload.parentOrganizationGroupId,
        }),
      ),
    );

    const org = createOrgApiResponse.data;
    yield syncEntitiesAndGetResults(org, SCHEMA.organization);
    yield call(
      runSagaWithAuth(() =>
        organizationInviteApi.createOrganizationInvite(org.id, {
          email: payload.email,
          integratorId: payload.integratorId,
        }),
      ),
    );
    yield put<OrganizationActionTypes>(createOrganizationSuccess());
    yield put(push(`/organisations`));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_ORGANIZATION_STORE_KEY,
        message: 'Success! Organization Created!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<OrganizationActionTypes>(createOrganizationFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_ORGANIZATION_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* createOrganizationInvite({ payload }: { type: string; payload: CreateOrganizationInvitePayload }) {
  try {
    const organizationInviteApi: OrganizationInvitesApi = yield createOrganizationInviteApi();

    yield call(
      runSagaWithAuth(() =>
        organizationInviteApi.createOrganizationInvite(payload.organizationId, {
          email: payload.email,
          integratorId: payload.integratorId,
        }),
      ),
    );
    yield put<OrganizationActionTypes>(createOrganizationInviteSuccess());
    yield put(fetchOrganizationInvitesRequested(payload.organizationId));
    yield put(fetchOrganizationUsersRequested(payload.organizationId));
    yield put(setCurrentModal(''));
    const flashMessage = payload.integratorId
      ? 'Success! Organization User Onboarded For Integrator!'
      : 'Success! Organization Invite Sent!';
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_ORGANIZATION_INVITE_STORE_KEY,
        message: flashMessage,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<OrganizationActionTypes>(createOrganizationInviteFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_ORGANIZATION_INVITE_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* updateOrganizationNotes({ payload }: { payload: UpdateOrganizationNotesPayload; type: string }) {
  try {
    const { organizationId, notes } = payload;
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const response: AxiosResponse<OrganizationDTO> = yield call(
      runSagaWithAuth(() => organizationsApi.updateOrganizationNotes(organizationId, { notes })),
    );

    yield syncEntitiesAndGetResults(response.data, SCHEMA.organization);

    yield put<OrganizationActionTypes>(updateOrganizationNotesSuccess());
    const flashMessage = 'Success! Organization notes updated!';
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORG_NOTES_STORE_KEY,
        message: flashMessage,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UPDATE_ORG_NOTES_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put(updateOrganizationNotesFailed(get(e, 'response.data.message', e)));
  }
}

export function* updateOrganizationAddress({ payload }: { payload: UpdateOrganizationAddressPayload; type: string }) {
  try {
    const { organizationId, address } = payload;
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const response: AxiosResponse<OrganizationDTO> = yield call(
      runSagaWithAuth(() => organizationsApi.updateOrganizationAddress(organizationId, { address })),
    );

    yield syncEntitiesAndGetResults(response.data, SCHEMA.organization);

    yield put<OrganizationActionTypes>(updateOrganizationAddressSuccess());
    const flashMessage = 'Success! Organization address updated!';
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORG_ADDRESS_STORE_KEY,
        message: flashMessage,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UPDATE_ORG_ADDRESS_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put(updateOrganizationAddressFailed(get(e, 'response.data.message', e)));
  }
}

interface OrganizationModalUpdate {
  request: (organizationsApi: OrganizationsApi) => Promise<AxiosResponse<OrganizationDTO>>;
  storeKey: string;
  modalStoreKey: string;
  successMessage: string;
  succeeded: ActionCreatorWithoutPayload;
  failed: ActionCreatorWithPayload<string>;
}

function* updateOrganizationFromModal({
  request,
  storeKey,
  modalStoreKey,
  successMessage,
  succeeded,
  failed,
}: OrganizationModalUpdate) {
  try {
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const response: AxiosResponse<OrganizationDTO> = yield call(runSagaWithAuth(() => request(organizationsApi)));

    yield syncEntitiesAndGetResults(response.data, SCHEMA.organization);
    yield put(succeeded());
    yield put<FlashMessagesActionTypes>(dismissFlashMessage([modalStoreKey]));
    yield put(setCurrentModal(''));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: storeKey,
        message: successMessage,
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(failed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: modalStoreKey,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* updateOrganizationPricingRule({ payload }: { payload: UpdateOrganizationPricingRulePayload; type: string }) {
  const { organizationId, pricingRule } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) =>
      organizationsApi.updateOrganizationPricingRule(organizationId, {
        pricingRule: updateOrganizationPricingRuleEnumBySettingsEnum[pricingRule],
      }),
    storeKey: UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_PRICING_RULE_MODAL_STORE_KEY,
    successMessage: 'Success! Pricing fee updated!',
    succeeded: updateOrganizationPricingRuleSuccess,
    failed: updateOrganizationPricingRuleFailed,
  });
}

export function* updateOrganizationPolicyCoverCap({
  payload,
}: {
  payload: UpdateOrganizationPolicyCoverCapPayload;
  type: string;
}) {
  const { organizationId, maximumPolicyCover } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) =>
      organizationsApi.updateOrganizationPolicyCoverCap(organizationId, {
        maximumPolicyCover: updateOrganizationPolicyCoverCapEnumBySettingsEnum[maximumPolicyCover] as any,
      }),
    storeKey: UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_POLICY_COVER_CAP_MODAL_STORE_KEY,
    successMessage: 'Success! Policy cover cap updated!',
    succeeded: updateOrganizationPolicyCoverCapSuccess,
    failed: updateOrganizationPolicyCoverCapFailed,
  });
}

export function* updateOrganizationCommissionRule({
  payload,
}: {
  payload: UpdateOrganizationCommissionRulePayload;
  type: string;
}) {
  const { organizationId, commissionRule } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) =>
      organizationsApi.updateOrganizationCommissionRule(organizationId, {
        commissionRule: updateOrganizationCommissionRuleEnumBySettingsEnum[commissionRule],
      }),
    storeKey: UPDATE_ORGANIZATION_COMMISSION_RULE_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_COMMISSION_RULE_MODAL_STORE_KEY,
    successMessage: 'Success! Commission rate updated!',
    succeeded: updateOrganizationCommissionRuleSuccess,
    failed: updateOrganizationCommissionRuleFailed,
  });
}

export function* updateOrganizationHubspotId({ payload }: { payload: UpdateOrganizationHubspotIdPayload; type: string }) {
  const { organizationId, hubspotId } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) => organizationsApi.updateOrganizationHubspotId(organizationId, { hubspotId }),
    storeKey: UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_HUBSPOT_ID_MODAL_STORE_KEY,
    successMessage: 'Success! HubSpot ID updated!',
    succeeded: updateOrganizationHubspotIdSuccess,
    failed: updateOrganizationHubspotIdFailed,
  });
}

export function* updateOrganizationName({ payload }: { payload: UpdateOrganizationNamePayload; type: string }) {
  const { organizationId, name } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) => organizationsApi.updateOrganizationName(organizationId, { name }),
    storeKey: UPDATE_ORGANIZATION_NAME_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_NAME_MODAL_STORE_KEY,
    successMessage: 'Success! Organisation name updated!',
    succeeded: updateOrganizationNameSuccess,
    failed: updateOrganizationNameFailed,
  });
}

export function* updateOrganizationInventoryConfirmationDelay({
  payload,
}: {
  payload: UpdateOrganizationInventoryConfirmationDelayPayload;
  type: string;
}) {
  const { organizationId, delayedInventoryConfirmationEnabled } = payload;
  yield* updateOrganizationFromModal({
    request: (organizationsApi) =>
      organizationsApi.updateOrganizationInventoryConfirmationDelay(organizationId, { delayedInventoryConfirmationEnabled }),
    storeKey: UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_STORE_KEY,
    modalStoreKey: UPDATE_ORGANIZATION_INVENTORY_CONFIRMATION_DELAY_MODAL_STORE_KEY,
    successMessage: 'Success! Inventory confirmation timing updated!',
    succeeded: updateOrganizationInventoryConfirmationDelaySuccess,
    failed: updateOrganizationInventoryConfirmationDelayFailed,
  });
}

export function* searchHubspotCompanies({ payload }: { payload: SearchHubspotCompaniesPayload; type: string }) {
  try {
    if (payload.query.trim().length < 2) {
      yield put<OrganizationActionTypes>(searchHubspotCompaniesSuccess([]));
      return;
    }

    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const response: AxiosResponse<SearchHubspotCompaniesResultsDTO> = yield call(
      runSagaWithAuth(() => organizationsApi.searchHubspotCompanies(payload.query)),
    );

    yield put<OrganizationActionTypes>(searchHubspotCompaniesSuccess(response.data.results));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<OrganizationActionTypes>(searchHubspotCompaniesFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORGANIZATION_HUBSPOT_ID_MODAL_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      }),
    );
  }
}

export function* fetchOrganizationUsersWithRoles({ payload }: { type: string; payload: string }) {
  try {
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const apiResponse: AxiosResponse<OrganizationUserDTO[]> = yield call(
      runSagaWithAuth(() => organizationsApi.fetchOrganizationUsersByOrganizationId(payload)),
    );
    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.organizationUsers);
    yield put<OrganizationActionTypes>(fetchOrganizationUsersWithRolesSuccess(apiResponse.data.map((orgUser) => orgUser.id)));
  } catch (e) {
    yield put<OrganizationActionTypes>(fetchOrganizationUsersWithRolesFailed(get(e, 'response.data.message', e)));
  }
}

// ****************
// WATCHERS
// ****************
export function* watchOrganizationSagas() {
  yield takeLatest(FETCH_ORGANIZATION_BY_ID_API_REQUESTED, fetchOrganizationById);
  yield takeLatest(CREATE_ORGANIZATION_API_REQUESTED, createOrganization);
  yield takeLatest(CREATE_ORGANIZATION_INVITE_API_REQUESTED, createOrganizationInvite);
  yield takeLatest(updateOrganizationNotesRequested.type, updateOrganizationNotes);
  yield takeLatest(updateOrganizationAddressRequested.type, updateOrganizationAddress);
  yield takeLatest(updateOrganizationPricingRuleRequested.type, updateOrganizationPricingRule);
  yield takeLatest(updateOrganizationPolicyCoverCapRequested.type, updateOrganizationPolicyCoverCap);
  yield takeLatest(updateOrganizationCommissionRuleRequested.type, updateOrganizationCommissionRule);
  yield takeLatest(updateOrganizationHubspotIdRequested.type, updateOrganizationHubspotId);
  yield takeLatest(updateOrganizationNameRequested.type, updateOrganizationName);
  yield takeLatest(updateOrganizationInventoryConfirmationDelayRequested.type, updateOrganizationInventoryConfirmationDelay);
  yield takeLatest(searchHubspotCompaniesRequested.type, searchHubspotCompanies);
  yield takeLatest(FETCH_ORGANIZATION_USERS_WITH_ROLES_API_REQUESTED, fetchOrganizationUsersWithRoles);
}
